import React, { useState } from "react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { FiFilter } from "react-icons/fi";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";

export interface AuditFilterState {
  eventType: string;
  status: string;
  ipAddress: string;
}

interface AuditLogsFilterDropProps {
  filterState: AuditFilterState;
  setFilterState: React.Dispatch<React.SetStateAction<AuditFilterState>>;
  eventTypeOptions: string[];
  onApply: (filters: AuditFilterState) => void;
  onClear: () => void;
}

const AuditLogsFilterDrop: React.FC<AuditLogsFilterDropProps> = ({
  filterState,
  setFilterState,
  eventTypeOptions,
  onApply,
  onClear,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<AuditFilterState>(filterState);
  const [eventTypeSearch, setEventTypeSearch] = useState("");

  const handleOpen = (open: boolean) => {
    if (open) {
      setLocalFilters(filterState); // sync local with current applied filters
    }
    setIsOpen(open);
  };

  const handleApply = () => {
    setFilterState(localFilters);
    onApply(localFilters);
    setIsOpen(false);
  };

  const handleClear = () => {
    const cleared: AuditFilterState = { eventType: "", status: "", ipAddress: "" };
    setLocalFilters(cleared);
    setFilterState(cleared);
    onClear();
    setIsOpen(false);
  };

  const getFilterButtonText = () => {
    const active = [];
    if (filterState.eventType) active.push(filterState.eventType);
    if (filterState.status) active.push(filterState.status);
    if (filterState.ipAddress) active.push(`IP: ${filterState.ipAddress}`);
    if (active.length === 0) return "Filter";
    const text = active.join(", ");
    return text.length > 30 ? text.substring(0, 30) + "..." : text;
  };

  const hasActiveFilters =
    !!filterState.eventType || !!filterState.status || !!filterState.ipAddress;

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-auto font-medium text-[14px] py-2 px-3 !mt-0 whitespace-nowrap"
        >
          <FiFilter style={{ width: "14px", height: "14px" }} className="mr-1" />
          {getFilterButtonText()}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="w-72 p-4 rounded-md shadow-md space-y-4"
        side="left"
        align="start"
        sideOffset={8}
      >
        {/* Event Type */}
<div className="flex items-center w-full gap-2">
          <div className="w-1/3">
            <Label className="text-sm text-[#020617]">Event Type</Label>
          </div>
          <div className="w-2/3">
            <Select
              value={localFilters.eventType || "all"}
              onValueChange={(val) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  eventType: val === "all" ? "" : val,
                }))
              }
            >
              <SelectTrigger className="h-8 text-sm text-gray-500 w-full">
                <SelectValue placeholder="All Events" />
              </SelectTrigger>
              <SelectContent>
                {/* Search Input */}
                <div className="flex items-center px-2 py-1 border-b border-gray-200">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={eventTypeSearch}
                    onChange={(e) => setEventTypeSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="w-full text-sm outline-none text-gray-700 placeholder-gray-400 py-1"
                  />
                </div>
                <SelectItem value="all">All Events</SelectItem>
                {eventTypeOptions
                  .filter((et) => et.toLowerCase().includes(eventTypeSearch.toLowerCase()))
                  .map((et) => (
                    <SelectItem key={et} value={et}>
                      {et}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center w-full gap-2">
          <div className="w-1/3">
            <Label className="text-sm text-[#020617]">Status</Label>
          </div>
          <div className="w-2/3">
            <Select
              value={localFilters.status || "all"}
              onValueChange={(val) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  status: val === "all" ? "" : val,
                }))
              }
            >
              <SelectTrigger className="h-8 text-sm text-gray-500 w-full">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Success">Success</SelectItem>
                <SelectItem value="Failure">Failure</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* IP Address */}
        <div className="flex items-center w-full gap-2">
          <div className="w-1/3">
            <Label className="text-sm text-[#020617]">IP Address</Label>
          </div>
          <div className="w-2/3">
            <Input
              placeholder="Filter by IP..."
              className="h-8 text-sm !mt-0"
              value={localFilters.ipAddress}
              onChange={(e) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  ipAddress: e.target.value,
                }))
              }
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-2 border-t border-gray-200">
          <Button
            variant="outline"
            className="w-[48%] h-8 text-sm !mt-0"
            onClick={handleClear}
          >
            Clear
          </Button>
          <Button
            className="w-[48%] h-8 text-sm !mt-0 bg-[#3A85F7] text-white hover:bg-blue-600"
            onClick={handleApply}
          >
            Apply
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AuditLogsFilterDrop;